/**
 * Foundation Identity — PersonDossier (Fascicolo Persona).
 *
 * Schermata UNICA dell'identita' della Persona all'interno di Gruppo Impresa Suite.
 * Ogni modulo (GI Presenze, GI Studio, GI Fatturazione, GI Finance, CRM, ...) apre
 * questa stessa schermata come /persone/:id.
 *
 * Sezioni:
 *   1. IDENTITA'      — anagrafica Persona
 *   2. ACCOUNT        — credenziali di accesso
 *   3. ASSEGNAZIONI   — Persona <-> Azienda <-> Ruolo
 *   4. RAPPORTI DI LAVORO — Employee collegati
 *   5. TIMELINE       — audit completo delle operazioni
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, User, KeyRound, Building2, Briefcase, History, Shield, ShieldCheck,
  Save, Mail, Copy, RefreshCw, Trash2, Plus, Clock, CheckCircle2, XCircle, AlertCircle,
  Loader2, IdCard, Calendar, Phone, Ban,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

import * as identityApi from "./api";
import { validateEmail } from "./validators";
import { api } from "../../lib/api";

const ROLE_LABELS = {
  super_admin: "ROOT (Super Admin)",
  admin: "ROOT (Super Admin)",
  studio_operator: "Consulente Studio",
  external_consultant: "Consulente esterno",
  company_admin: "Amministratore azienda",
  client: "Amministratore azienda",
  company_responsible: "Responsabile azienda",
  responsabile: "Responsabile azienda",
  employee: "Dipendente",
};

const ACCOUNT_STATUS_LABELS = {
  active: "Attivo",
  invited: "Invitato",
  suspended: "Sospeso",
  disabled: "Disabilitato",
};

const ACCOUNT_STATUS_TONE = {
  active: "bg-emerald-100 text-emerald-800",
  invited: "bg-blue-100 text-blue-800",
  suspended: "bg-amber-100 text-amber-800",
  disabled: "bg-slate-200 text-slate-700",
};

const EVENT_LABELS = {
  "identity.person.created": "Persona creata",
  "identity.person.updated": "Anagrafica aggiornata",
  "identity.person.deactivated": "Persona disattivata",
  "identity.account.created": "Account creato",
  "identity.account.updated": "Account aggiornato",
  "identity.account.password_reset": "Password reimpostata",
  "identity.assignment.added": "Assegnazione aggiunta",
  "identity.assignment.removed": "Assegnazione rimossa",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatDateOnly(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT");
  } catch {
    return iso;
  }
}

export default function PersonDossier() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [person, setPerson] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("identity");

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await identityApi.getPerson(id);
      setPerson(p);
      const [emp, tl] = await Promise.all([
        identityApi.getPersonEmployees(id).catch(() => ({ items: [] })),
        identityApi.getPersonTimeline(id).catch(() => ({ items: [] })),
      ]);
      setEmployees(emp.items || []);
      setTimeline(tl.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Persona non trovata");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // carica companies per assignments
    api.get("/companies").then(({ data }) => setCompanies(data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Caricamento fascicolo…
      </div>
    );
  }
  if (error || !person) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Fascicolo non disponibile</AlertTitle>
          <AlertDescription>{String(error || "Persona non trovata")}</AlertDescription>
        </Alert>
        <Button className="mt-4" variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Indietro
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="person-dossier">
      <Header person={person} onBack={() => navigate(-1)} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="identity" data-testid="dossier-tab-identity">
            <User className="mr-1 h-4 w-4" /> Identità
          </TabsTrigger>
          <TabsTrigger value="account" data-testid="dossier-tab-account">
            <KeyRound className="mr-1 h-4 w-4" /> Account
          </TabsTrigger>
          <TabsTrigger value="assignments" data-testid="dossier-tab-assignments">
            <Building2 className="mr-1 h-4 w-4" /> Assegnazioni
          </TabsTrigger>
          <TabsTrigger value="employment" data-testid="dossier-tab-employment">
            <Briefcase className="mr-1 h-4 w-4" /> Rapporti
          </TabsTrigger>
          <TabsTrigger value="timeline" data-testid="dossier-tab-timeline">
            <History className="mr-1 h-4 w-4" /> Timeline
          </TabsTrigger>
        </TabsList>

        <TabsContent value="identity" className="mt-4">
          <IdentitySection person={person} onSaved={refresh} />
        </TabsContent>
        <TabsContent value="account" className="mt-4">
          <AccountSection person={person} onSaved={refresh} />
        </TabsContent>
        <TabsContent value="assignments" className="mt-4">
          <AssignmentsSection person={person} companies={companies} onChanged={refresh} />
        </TabsContent>
        <TabsContent value="employment" className="mt-4">
          <EmploymentSection employees={employees} />
        </TabsContent>
        <TabsContent value="timeline" className="mt-4">
          <TimelineSection events={timeline} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Header
// ═══════════════════════════════════════════════════════════════════════════════
function Header({ person, onBack }) {
  return (
    <div className="flex flex-wrap items-start gap-4 justify-between">
      <div className="flex items-start gap-3">
        <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
          <User className="h-6 w-6 text-primary" />
        </div>
        <div>
          <div className="text-xs uppercase text-slate-500 tracking-wide">Fascicolo Persona</div>
          <h1 className="text-2xl font-bold" data-testid="dossier-full-name">
            {person.first_name} {person.last_name}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-slate-600">
            <IdCard className="h-4 w-4" />
            <span className="font-mono" data-testid="dossier-fiscal-code">{person.fiscal_code}</span>
            {person.active ? (
              <Badge variant="secondary" className="bg-emerald-100 text-emerald-800">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Attiva
              </Badge>
            ) : (
              <Badge variant="secondary" className="bg-slate-200 text-slate-700">
                <Ban className="h-3 w-3 mr-1" /> Disattivata
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack} data-testid="dossier-back-btn">
          <ArrowLeft className="mr-2 h-4 w-4" /> Torna alla lista
        </Button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Sezione 1 · IDENTITA'
// ═══════════════════════════════════════════════════════════════════════════════
function IdentitySection({ person, onSaved }) {
  const [firstName, setFirstName] = useState(person.first_name || "");
  const [lastName, setLastName] = useState(person.last_name || "");
  const [birthDate, setBirthDate] = useState(person.birth_date || "");
  const [email, setEmail] = useState(person.email || "");
  const [phone, setPhone] = useState(person.phone || "");
  const [notes, setNotes] = useState(person.notes || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await identityApi.updatePerson(person.id, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        birth_date: birthDate || null,
        email: email.trim() || null,
        phone: phone.trim() || null,
        notes: notes.trim() || null,
      });
      toast.success("Dati anagrafici aggiornati");
      onSaved?.();
    } catch (e) {
      toast.error("Salvataggio fallito", {
        description: e?.response?.data?.detail || "Errore imprevisto",
      });
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async () => {
    if (!window.confirm("Confermi la disattivazione della Persona? (soft-delete: nessun dato viene eliminato)")) return;
    try {
      await identityApi.deactivatePerson(person.id);
      toast.success("Persona disattivata");
      onSaved?.();
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5 text-primary" /> Identità Persona
        </CardTitle>
        <CardDescription>
          Dati anagrafici della Persona. Il Codice Fiscale è l'identificativo univoco e non è modificabile.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Codice Fiscale</label>
            <Input value={person.fiscal_code} disabled className="font-mono" data-testid="identity-cf" />
            <p className="text-xs text-slate-500 mt-1">Immutabile — identificativo naturale della Persona</p>
          </div>
          <div>
            <label className="text-sm font-medium">
              <Calendar className="inline h-3 w-3 mr-1" /> Data di nascita
            </label>
            <Input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)}
                     data-testid="identity-birth-date" />
          </div>
          <div>
            <label className="text-sm font-medium">Nome</label>
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                     data-testid="identity-first-name" />
          </div>
          <div>
            <label className="text-sm font-medium">Cognome</label>
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)}
                     data-testid="identity-last-name" />
          </div>
          <div>
            <label className="text-sm font-medium">
              <Mail className="inline h-3 w-3 mr-1" /> Email contatto
            </label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     placeholder="Email personale (non necessariamente quella di accesso)"
                     data-testid="identity-email" />
          </div>
          <div>
            <label className="text-sm font-medium">
              <Phone className="inline h-3 w-3 mr-1" /> Telefono
            </label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                     data-testid="identity-phone" />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm font-medium">Note</label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                     placeholder="Note interne (facoltativo)"
                     data-testid="identity-notes" />
          </div>
        </div>
        <Separator />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            {person.active && (
              <Button variant="ghost" onClick={deactivate}
                        className="text-red-600 hover:text-red-700"
                        data-testid="identity-deactivate-btn">
                <Ban className="mr-2 h-4 w-4" /> Disattiva Persona
              </Button>
            )}
          </div>
          <Button onClick={save} disabled={busy} data-testid="identity-save-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Salva anagrafica
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Sezione 2 · ACCOUNT
// ═══════════════════════════════════════════════════════════════════════════════
function AccountSection({ person, onSaved }) {
  const account = person.account;
  const [username, setUsername] = useState(account?.username || "");
  const [email, setEmail] = useState(account?.email || "");
  const [status, setStatus] = useState(account?.account_status || "active");
  const [busy, setBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  useEffect(() => {
    setUsername(account?.username || "");
    setEmail(account?.email || "");
    setStatus(account?.account_status || "active");
  }, [account]);

  if (!account) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" /> Account di accesso
          </CardTitle>
          <CardDescription>Questa Persona non ha ancora un account associato.</CardDescription>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Per abilitare l'accesso, usa la funzione "Invita utente" nella lista Persone.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const saveAccount = async () => {
    setBusy(true);
    try {
      const emailV = validateEmail(email);
      if (!emailV.valid) {
        toast.error("Email non valida", { description: emailV.error });
        setBusy(false);
        return;
      }
      await identityApi.updateAccount(person.id, {
        username: username.trim(),
        email: emailV.normalized,
        account_status: status,
      });
      toast.success("Account aggiornato");
      onSaved?.();
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary" /> Account di accesso
        </CardTitle>
        <CardDescription>
          Credenziali di autenticazione. Cambiando lo stato dell'account puoi sospendere o riabilitare l'accesso.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Username</label>
            <Input value={username} onChange={(e) => setUsername(e.target.value)}
                     data-testid="account-username" />
          </div>
          <div>
            <label className="text-sm font-medium">Email di accesso</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     data-testid="account-email" />
          </div>
          <div>
            <label className="text-sm font-medium">Stato account</label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="account-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Attivo</SelectItem>
                <SelectItem value="invited">Invitato</SelectItem>
                <SelectItem value="suspended">Sospeso</SelectItem>
                <SelectItem value="disabled">Disabilitato</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Ultimo accesso</label>
            <div className="mt-2 text-sm text-slate-700 flex items-center gap-1" data-testid="account-last-login">
              <Clock className="h-4 w-4 text-slate-400" />
              {account.last_login_at ? formatDate(account.last_login_at) : "Mai effettuato"}
            </div>
          </div>
        </div>
        <Separator />
        {/* MFA scaffold */}
        <div className="rounded-md border p-4 bg-slate-50">
          <div className="flex items-start justify-between gap-4">
            <div className="flex gap-3">
              {account.mfa_enabled ? (
                <ShieldCheck className="h-5 w-5 text-emerald-600" />
              ) : (
                <Shield className="h-5 w-5 text-slate-400" />
              )}
              <div>
                <div className="font-medium">Autenticazione a due fattori (MFA)</div>
                <div className="text-xs text-slate-500">
                  {account.mfa_enabled
                    ? "MFA attiva su questo account"
                    : "Prossimamente: TOTP e passkey saranno gestibili da qui"}
                </div>
              </div>
            </div>
            <Button variant="outline" size="sm" disabled data-testid="account-mfa-toggle">
              {account.mfa_enabled ? "Disattiva" : "Attiva (prossimamente)"}
            </Button>
          </div>
        </div>
        <Separator />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setResetOpen(true)}
                      data-testid="account-reset-password-btn">
              <RefreshCw className="mr-2 h-4 w-4" /> Reimposta password
            </Button>
          </div>
          <Button onClick={saveAccount} disabled={busy} data-testid="account-save-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Salva account
          </Button>
        </div>

        <PasswordResetDialog
          open={resetOpen}
          onOpenChange={setResetOpen}
          personId={person.id}
          onDone={(res) => {
            setResetResult(res);
            onSaved?.();
          }}
        />
        {resetResult && (
          <Alert className="bg-amber-50 border-amber-200">
            {resetResult.provisional_password ? (
              <>
                <KeyRound className="h-4 w-4 text-amber-600" />
                <AlertTitle>Password provvisoria generata</AlertTitle>
                <AlertDescription>
                  <div className="flex items-center gap-2 mt-2">
                    <code className="px-2 py-1 rounded bg-slate-800 text-white font-mono text-sm"
                              data-testid="reset-result-password">
                      {resetResult.provisional_password}
                    </code>
                    <Button size="sm" variant="outline"
                              onClick={() => { navigator.clipboard.writeText(resetResult.provisional_password); toast.success("Copiata"); }}>
                      <Copy className="h-3 w-3 mr-1" /> Copia
                    </Button>
                  </div>
                  <p className="text-xs mt-2">L'utente dovrà cambiarla al primo accesso.</p>
                </AlertDescription>
              </>
            ) : (
              <>
                <Mail className="h-4 w-4 text-blue-600" />
                <AlertTitle>Link di attivazione</AlertTitle>
                <AlertDescription>
                  <div className="text-xs break-all bg-white/60 rounded p-2 font-mono mt-2"
                            data-testid="reset-result-link">
                    {resetResult.invite_link}
                  </div>
                </AlertDescription>
              </>
            )}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}

function PasswordResetDialog({ open, onOpenChange, personId, onDone }) {
  const [method, setMethod] = useState("password");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await identityApi.resetAccountPassword(personId, { method });
      onDone?.(res);
      onOpenChange(false);
      toast.success("Password reimpostata");
    } catch (e) {
      toast.error("Reset fallito", { description: e?.response?.data?.detail });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent formDialog data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle>Reimposta password</DialogTitle>
          <DialogDescription>Scegli il metodo di riemissione delle credenziali.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <label className={"flex gap-3 rounded-md border p-3 cursor-pointer " + (method === "password" ? "border-primary bg-primary/5" : "")}>
            <input type="radio" checked={method === "password"} onChange={() => setMethod("password")} data-testid="reset-method-password" />
            <div>
              <div className="font-medium">Password provvisoria auto-generata</div>
              <div className="text-xs text-slate-500">La riceverai qui e potrai comunicarla in modo sicuro.</div>
            </div>
          </label>
          <label className={"flex gap-3 rounded-md border p-3 cursor-pointer " + (method === "link" ? "border-primary bg-primary/5" : "")}>
            <input type="radio" checked={method === "link"} onChange={() => setMethod("link")} data-testid="reset-method-link" />
            <div>
              <div className="font-medium">Link di attivazione via email</div>
              <div className="text-xs text-slate-500">L'utente sceglie una nuova password autonomamente.</div>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={busy} data-testid="reset-submit-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Conferma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Sezione 3 · ASSEGNAZIONI
// ═══════════════════════════════════════════════════════════════════════════════
function AssignmentsSection({ person, companies, onChanged }) {
  const [addOpen, setAddOpen] = useState(false);

  const remove = async (assignmentId) => {
    if (!window.confirm("Rimuovere questa assegnazione?")) return;
    try {
      await identityApi.removeAssignment(person.id, assignmentId);
      toast.success("Assegnazione rimossa");
      onChanged?.();
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail });
    }
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" /> Assegnazioni
          </CardTitle>
          <CardDescription>
            Coppie Azienda + Ruolo attribuite alla Persona. Ogni riga determina cosa può fare l'utente e in quale ambito.
          </CardDescription>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)} disabled={!person.account}
                  data-testid="assignments-add-btn">
          <Plus className="mr-2 h-4 w-4" /> Aggiungi assegnazione
        </Button>
      </CardHeader>
      <CardContent>
        {!person.account ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>La Persona deve avere un account attivo prima di ricevere assegnazioni.</AlertDescription>
          </Alert>
        ) : (person.assignments || []).length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center">Nessuna assegnazione attiva.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Azienda</TableHead>
                <TableHead>Ruolo</TableHead>
                <TableHead>Assegnata il</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="w-16 text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {person.assignments.map((a) => (
                <TableRow key={a.id} data-testid={`assignment-row-${a.id}`}>
                  <TableCell>{a.company_name || <span className="text-slate-400">Globale</span>}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{ROLE_LABELS[a.role] || a.role}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">{formatDateOnly(a.created_at)}</TableCell>
                  <TableCell>
                    {a.active ? (
                      <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle2 className="h-3 w-3 mr-1" /> Attiva</Badge>
                    ) : (
                      <Badge className="bg-slate-200 text-slate-700">Inattiva</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => remove(a.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`assignment-remove-${a.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <AddAssignmentDialog open={addOpen} onOpenChange={setAddOpen}
                                personId={person.id} companies={companies}
                                onAdded={onChanged} />
      </CardContent>
    </Card>
  );
}

function AddAssignmentDialog({ open, onOpenChange, personId, companies, onAdded }) {
  const [role, setRole] = useState("company_admin");
  const [companyId, setCompanyId] = useState("");
  const [busy, setBusy] = useState(false);

  const isGlobal = role === "super_admin" || role === "studio_operator";

  const submit = async () => {
    if (!isGlobal && !companyId) {
      toast.error("Seleziona un'azienda");
      return;
    }
    setBusy(true);
    try {
      await identityApi.addAssignment(personId, {
        company_id: isGlobal ? null : companyId,
        role,
      });
      toast.success("Assegnazione aggiunta");
      onOpenChange(false);
      onAdded?.();
    } catch (e) {
      toast.error("Errore", { description: e?.response?.data?.detail });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent formDialog data-testid="add-assignment-dialog">
        <DialogHeader>
          <DialogTitle>Nuova assegnazione</DialogTitle>
          <DialogDescription>Definisci Azienda e Ruolo per la Persona.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">Ruolo</label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="add-assignment-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="super_admin">ROOT (Super Admin)</SelectItem>
                <SelectItem value="studio_operator">Consulente Studio</SelectItem>
                <SelectItem value="external_consultant">Consulente esterno</SelectItem>
                <SelectItem value="company_admin">Amministratore azienda</SelectItem>
                <SelectItem value="company_responsible">Responsabile azienda</SelectItem>
                <SelectItem value="employee">Dipendente</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Azienda</label>
            <Select value={companyId} onValueChange={setCompanyId} disabled={isGlobal}>
              <SelectTrigger data-testid="add-assignment-company">
                <SelectValue placeholder={isGlobal ? "Non richiesta" : "Seleziona azienda"} />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={busy} data-testid="add-assignment-submit">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Aggiungi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Sezione 4 · RAPPORTI DI LAVORO
// ═══════════════════════════════════════════════════════════════════════════════
function EmploymentSection({ employees }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-primary" /> Rapporti di lavoro
        </CardTitle>
        <CardDescription>
          Employee (rapporti di lavoro) della Persona all'interno della Suite. Una Persona può essere dipendente in più aziende.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {employees.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center">
            Nessun rapporto di lavoro registrato per questa Persona.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Azienda</TableHead>
                <TableHead>Matricola</TableHead>
                <TableHead>Qualifica</TableHead>
                <TableHead>Assunzione</TableHead>
                <TableHead>Cessazione</TableHead>
                <TableHead>Stato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((e) => (
                <TableRow key={e.id} data-testid={`employment-row-${e.id}`}>
                  <TableCell>{e.company_name || "—"}</TableCell>
                  <TableCell className="font-mono">{e.matricola || "—"}</TableCell>
                  <TableCell>{e.qualification || "—"}</TableCell>
                  <TableCell>{formatDateOnly(e.hire_date)}</TableCell>
                  <TableCell>{e.cessation_date ? formatDateOnly(e.cessation_date) : "—"}</TableCell>
                  <TableCell>
                    {e.cessation_date ? (
                      <Badge variant="secondary" className="bg-slate-200">Cessato</Badge>
                    ) : e.active ? (
                      <Badge className="bg-emerald-100 text-emerald-800">Attivo</Badge>
                    ) : (
                      <Badge variant="secondary">Inattivo</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Sezione 5 · TIMELINE
// ═══════════════════════════════════════════════════════════════════════════════
function TimelineSection({ events }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5 text-primary" /> Timeline
        </CardTitle>
        <CardDescription>
          Storico completo delle operazioni sulla Persona (audit log Foundation Identity).
        </CardDescription>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <div className="text-sm text-slate-500 py-4 text-center">Nessun evento registrato.</div>
        ) : (
          <ol className="relative border-l border-slate-200 ml-3 space-y-4">
            {events.map((ev, idx) => (
              <li key={idx} className="ml-4" data-testid={`timeline-item-${idx}`}>
                <div className="absolute w-2 h-2 bg-primary rounded-full mt-1.5 -left-1" />
                <time className="mb-1 text-xs font-normal text-slate-400">
                  {formatDate(ev.created_at)}
                </time>
                <h3 className="text-sm font-semibold">
                  {EVENT_LABELS[ev.event] || ev.event}
                </h3>
                {ev.meta && Object.keys(ev.meta).length > 0 && (
                  <p className="text-xs text-slate-600">
                    {Object.entries(ev.meta).map(([k, v]) => (
                      <span key={k} className="mr-3">
                        <span className="text-slate-400">{k}:</span> {String(v)}
                      </span>
                    ))}
                  </p>
                )}
              </li>
            ))}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
